import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { StatusBadge } from '@/components/StatusBadge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar as CalIcon, ChevronLeft, ChevronRight, Plus, MapPin } from 'lucide-react';
import { fmtTime } from '@/lib/dates';
import { addMonths, addWeeks, addDays, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, format, isSameMonth, isSameDay, parseISO, isToday } from 'date-fns';
import { id as idLocale } from 'date-fns/locale';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/lib/auth';

export default function AgendaCalendarPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month'); // month | week

  useEffect(() => {
    api.get('/agenda').then((r) => setItems(r.data));
  }, []);

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });

  const weekDays = eachDayOfInterval({
    start: startOfWeek(currentDate, { weekStartsOn: 1 }),
    end: endOfWeek(currentDate, { weekStartsOn: 1 }),
  });

  const eventsByDate = useMemo(() => {
    const map = {};
    items.forEach((a) => {
      try {
        const d = format(parseISO(a.tanggal_mulai), 'yyyy-MM-dd');
        if (!map[d]) map[d] = [];
        map[d].push(a);
      } catch {}
    });
    return map;
  }, [items]);

  const goPrev = () => setCurrentDate(view === 'month' ? addMonths(currentDate, -1) : addWeeks(currentDate, -1));
  const goNext = () => setCurrentDate(view === 'month' ? addMonths(currentDate, 1) : addWeeks(currentDate, 1));
  const goToday = () => setCurrentDate(new Date());

  const canCreate = user?.role === 'admin' || user?.role === 'operator';
  const statusColor = { Pending: '#F59E0B', Approved: '#10B981', Rejected: '#EF4444', 'Sedang Berlangsung': '#0EA5E9', Selesai: '#94A3B8' };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Kalender Agenda"
        subtitle="Lihat agenda pimpinan dalam tampilan kalender bulanan dan mingguan."
        breadcrumb={[{ label: 'Beranda', to: '/dashboard' }, { label: 'Agenda', to: '/agenda' }, { label: 'Kalender' }]}
        action={canCreate && (
          <Link to="/agenda/baru"><Button className="bg-[#DC2626] hover:bg-[#B91C1C]"><Plus className="w-4 h-4 mr-2" />Tambah Agenda</Button></Link>
        )}
      />

      <Card className="border-slate-200 shadow-premium">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={goPrev} data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
              <Button variant="outline" onClick={goToday} data-testid="cal-today">Hari Ini</Button>
              <Button variant="outline" size="icon" onClick={goNext} data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Button>
              <h2 className="ml-3 text-lg md:text-xl font-extrabold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                {format(currentDate, 'MMMM yyyy', { locale: idLocale })}
              </h2>
            </div>
            <Tabs value={view} onValueChange={setView}>
              <TabsList>
                <TabsTrigger value="month" data-testid="cal-view-month">Bulan</TabsTrigger>
                <TabsTrigger value="week" data-testid="cal-view-week">Minggu</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {view === 'month' ? (
            <div className="overflow-x-auto -mx-2 sm:mx-0 px-2 sm:px-0 pb-2">
              <div className="min-w-[640px] lg:min-w-0 grid grid-cols-7 border-l border-t border-slate-200 rounded-lg overflow-hidden">
                {['Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab', 'Min'].map((d) => (
                  <div key={d} className="px-2 py-2 text-xs font-semibold text-slate-600 bg-slate-50 border-r border-b border-slate-200 text-center">
                    {d}
                  </div>
                ))}
                {days.map((day) => {
                  const k = format(day, 'yyyy-MM-dd');
                  const events = eventsByDate[k] || [];
                  const inMonth = isSameMonth(day, currentDate);
                  const today = isToday(day);
                  return (
                    <div
                      key={k}
                      className={`min-h-[110px] border-r border-b border-slate-200 p-1.5 cursor-pointer transition-colors ${inMonth ? 'bg-white' : 'bg-slate-50/60'} hover:bg-red-50/40`}
                    >
                      <div className={`text-xs font-semibold mb-1 ${today ? 'inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#DC2626] text-white' : inMonth ? 'text-slate-700' : 'text-slate-400'}`}>
                        {format(day, 'd')}
                      </div>
                      <div className="space-y-1">
                        {events.slice(0, 3).map((e) => (
                          <div
                            key={e.id}
                            onClick={() => navigate(`/agenda/${e.id}`)}
                            className="text-[10px] px-1.5 py-1 rounded font-medium text-white cursor-pointer truncate hover:opacity-90"
                            style={{ background: statusColor[e.status] || '#94A3B8' }}
                            title={e.nama_acara}
                          >
                            <span className="font-mono">{fmtTime(e.tanggal_mulai)}</span> {e.nama_acara}
                          </div>
                        ))}
                        {events.length > 3 && (
                          <div className="text-[10px] text-slate-500 font-medium px-1">+{events.length - 3} lagi</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {weekDays.map((day) => {
                const k = format(day, 'yyyy-MM-dd');
                const events = eventsByDate[k] || [];
                return (
                  <div key={k} className={`flex items-start gap-4 p-3 rounded-lg border ${isToday(day) ? 'border-red-300 bg-red-50/30' : 'border-slate-200 bg-white'}`}>
                    <div className="w-20 flex-shrink-0">
                      <div className="text-xs text-slate-500 uppercase">{format(day, 'EEEE', { locale: idLocale })}</div>
                      <div className={`text-2xl font-extrabold ${isToday(day) ? 'text-[#DC2626]' : 'text-slate-900'}`}>{format(day, 'd')}</div>
                      <div className="text-xs text-slate-500">{format(day, 'MMM', { locale: idLocale })}</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      {events.length === 0 ? (
                        <div className="text-sm text-slate-400 italic">Tidak ada agenda</div>
                      ) : (
                        <div className="space-y-1.5">
                          {events.map((e) => (
                            <Link key={e.id} to={`/agenda/${e.id}`} className="flex items-center gap-3 p-2 rounded hover:bg-slate-100">
                              <span className="w-1 h-8 rounded" style={{ background: statusColor[e.status] }} />
                              <div className="font-mono text-xs font-semibold w-12 text-slate-600">{fmtTime(e.tanggal_mulai)}</div>
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold text-sm truncate">{e.nama_acara}</div>
                                <div className="text-xs text-slate-500 truncate"><MapPin className="w-3 h-3 inline" /> {e.lokasi}</div>
                              </div>
                              <StatusBadge status={e.status} type="agenda" />
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-xs text-slate-600">
        {Object.entries(statusColor).map(([k, v]) => (
          <div key={k} className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded" style={{ background: v }} />
            <span>{k}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
